import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { 
  ArrowLeft, 
  Edit, 
  FileText, 
  User, 
  Building,
  Calendar,
  MapPin,
  Phone,
  Mail,
  Send,
  Paperclip,
  Download,
  Eye,
  MessageSquare,
  Clock,
  CheckCircle,
  AlertCircle,
  XCircle,
  Plus,
  Filter
} from 'lucide-react';
import { dossierDetailData } from '../../data/mockData';

// Données importées depuis mockData
const dossierDetails = dossierDetailData;

const DossierDetail = () => {
  const { id } = useParams();
  const [activeTab, setActiveTab] = useState('informations');
  const [newComment, setNewComment] = useState('');
  const [interactionFilter, setInteractionFilter] = useState('all');
  const [showCommentForm, setShowCommentForm] = useState(false);
  
  const dossier = dossierDetailData[id];

  if (!dossier) {
    return (
      <div className="text-center py-12">
        <FileText className="h-12 w-12 text-port-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-port-gray-900 mb-2">Dossier non trouvé</h3>
        <Link to="/admin/dossiers" className="btn btn-primary">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Retour aux dossiers
        </Link>
      </div>
    );
  }

  const getStatutBadge = (statut) => {
    const badges = {
      'ouvert': { class: 'badge-danger', label: 'Ouvert', icon: XCircle },
      'attribue': { class: 'badge-info', label: 'Attribué', icon: Clock },
      'en_cours': { class: 'badge-warning', label: 'En cours', icon: Clock },
      'cloture': { class: 'badge-success', label: 'Clôturé', icon: CheckCircle }
    };
    return badges[statut];
  };

  const getInteractionIcon = (type) => {
    const icons = {
      'creation': Clock,
      'commentaire': MessageSquare,
      'document': Paperclip,
      'statut': AlertCircle
    };
    return icons[type] || MessageSquare;
  };

  const filteredInteractions = dossier.interactions.filter(interaction => {
    if (interactionFilter === 'all') return true;
    return interaction.type === interactionFilter;
  });

  const handleCommentSubmit = (e) => {
    e.preventDefault();
    if (newComment.trim()) {
      // Ici on ajouterait le commentaire via API
      console.log('Nouveau commentaire:', newComment);
      setNewComment('');
      setShowCommentForm(false);
    }
  };

  const handleGeneratePDF = () => {
    // Ici on génèrerait le PDF
    alert('Génération du rapport PDF en cours...');
  };

  const statutBadge = getStatutBadge(dossier.statut);
  const StatutIcon = statutBadge.icon;

  return (
    <div className="animate-slide-in">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <Link 
            to="/admin/dossiers" 
            className="p-2 text-port-gray-600 hover:bg-port-gray-100 rounded-lg transition-colors mr-4"
          >
            <ArrowLeft className="h-5 w-5" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-port-gray-900">{dossier.reference}</h1>
            <p className="text-port-gray-600 flex items-center mt-1">
              <span className={`badge ${statutBadge.class} mr-2`}>
                <StatutIcon className="h-3 w-3 mr-1" />
                {statutBadge.label}
              </span>
              {dossier.sinistre.type} - {dossier.sinistre.nature}
            </p>
          </div>
        </div>
        
        <div className="flex items-center space-x-3">
          <button
            onClick={handleGeneratePDF}
            className="btn btn-success"
          >
            <Download className="h-4 w-4 mr-2" />
            Générer PDF
          </button>
          <button className="btn btn-secondary">
            <Edit className="h-4 w-4 mr-2" />
            Modifier
          </button>
        </div>
      </div>

      {/* Onglets */}
      <div className="border-b border-port-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('informations')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'informations'
                ? 'border-atl-blue-500 text-atl-blue-600'
                : 'border-transparent text-port-gray-500 hover:text-port-gray-700 hover:border-port-gray-300'
            }`}
          >
            Informations
          </button>
          <button
            onClick={() => setActiveTab('interactions')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'interactions'
                ? 'border-atl-blue-500 text-atl-blue-600'
                : 'border-transparent text-port-gray-500 hover:text-port-gray-700 hover:border-port-gray-300'
            }`}
          >
            Interactions ({dossier.interactions.length})
          </button>
          <button
            onClick={() => setActiveTab('documents')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'documents'
                ? 'border-atl-blue-500 text-atl-blue-600'
                : 'border-transparent text-port-gray-500 hover:text-port-gray-700 hover:border-port-gray-300'
            }`}
          >
            Documents ({dossier.documentsTransmis.length})
          </button>
        </nav>
      </div>

      {/* Contenu des onglets */}
      {activeTab === 'informations' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Informations sur le sinistre */}
          <div className="card">
            <div className="card-header">
              <h3 className="text-lg font-medium flex items-center">
                <AlertCircle className="h-5 w-5 mr-2" />
                Détails du Sinistre
              </h3>
            </div>
            <div className="card-body space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="form-label">Type</label>
                  <p className="text-port-gray-900">{dossier.sinistre.type}</p>
                </div>
                <div>
                  <label className="form-label">Nature</label>
                  <p className="text-port-gray-900">{dossier.sinistre.nature}</p>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="form-label flex items-center">
                    <Calendar className="h-4 w-4 mr-1" />
                    Date
                  </label>
                  <p className="text-port-gray-900">
                    {new Date(dossier.sinistre.date).toLocaleDateString('fr-FR')}
                  </p>
                </div>
                <div>
                  <label className="form-label">Heure</label>
                  <p className="text-port-gray-900">{dossier.sinistre.heure}</p>
                </div>
              </div>

              <div>
                <label className="form-label flex items-center">
                  <MapPin className="h-4 w-4 mr-1" />
                  Lieu
                </label>
                <p className="text-port-gray-900">{dossier.sinistre.lieu}</p>
              </div>

              <div>
                <label className="form-label">Description</label>
                <p className="text-port-gray-900">{dossier.sinistre.description}</p>
              </div>

              <div>
                <label className="form-label">Montant estimé</label>
                <p className="text-2xl font-bold text-atl-blue-600">
                  {dossier.montantEstime.toLocaleString('fr-FR')} €
                </p>
              </div>
            </div>
          </div>

          {/* Informations client */}
          <div className="card">
            <div className="card-header">
              <h3 className="text-lg font-medium flex items-center">
                <Building className="h-5 w-5 mr-2" />
                Client
              </h3>
            </div>
            <div className="card-body space-y-4">
              <div>
                <label className="form-label">Compagnie</label>
                <Link 
                  to={`/admin/clients/${dossier.client.id}`}
                  className="text-atl-blue-600 hover:underline font-medium"
                >
                  {dossier.client.nom}
                </Link>
              </div>

              <div>
                <label className="form-label">Contact principal</label>
                <p className="text-port-gray-900">{dossier.client.contact}</p>
              </div>

              <div className="grid grid-cols-1 gap-3">
                <div className="flex items-center">
                  <Phone className="h-4 w-4 text-port-gray-400 mr-2" />
                  <span className="text-port-gray-900">{dossier.client.telephone}</span>
                </div>
                <div className="flex items-center">
                  <Mail className="h-4 w-4 text-port-gray-400 mr-2" />
                  <a href={`mailto:${dossier.client.email}`} className="text-atl-blue-600 hover:underline">
                    {dossier.client.email}
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Informations assuré */}
          <div className="card">
            <div className="card-header">
              <h3 className="text-lg font-medium flex items-center">
                <User className="h-5 w-5 mr-2" />
                Assuré ({dossier.assure.type === 'particulier' ? 'Particulier' : 'Société'})
              </h3>
            </div>
            <div className="card-body space-y-4">
              {dossier.assure.type === 'particulier' ? (
                <>
                  <div>
                    <label className="form-label">Nom complet</label>
                    <p className="text-port-gray-900 font-medium">
                      {dossier.assure.prenom} {dossier.assure.nom}
                    </p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="form-label flex items-center">
                        <Calendar className="h-4 w-4 mr-1" />
                        Date de naissance
                      </label>
                      <p className="text-port-gray-900">
                        {dossier.assure.dateNaissance ? new Date(dossier.assure.dateNaissance).toLocaleDateString('fr-FR') : '-'}
                      </p>
                    </div>
                    <div>
                      <label className="form-label flex items-center">
                        <MapPin className="h-4 w-4 mr-1" />
                        Lieu de naissance
                      </label>
                      <p className="text-port-gray-900">{dossier.assure.lieuNaissance || '-'}</p>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div>
                    <label className="form-label flex items-center">
                      <Building className="h-4 w-4 mr-1" />
                      Raison sociale
                    </label>
                    <p className="text-port-gray-900 font-medium">{dossier.assure.raisonSociale}</p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="form-label">SIREN</label>
                      <p className="text-port-gray-900">{dossier.assure.siren || '-'}</p>
                    </div>
                    <div>
                      <label className="form-label">SIRET</label>
                      <p className="text-port-gray-900">{dossier.assure.siret || '-'}</p>
                    </div>
                  </div>
                </>
              )}

              <div>
                <label className="form-label">Adresse</label>
                <p className="text-port-gray-900">{dossier.assure.adresse}</p>
              </div>

              <div className="grid grid-cols-1 gap-3">
                <div className="flex items-center">
                  <Phone className="h-4 w-4 text-port-gray-400 mr-2" />
                  <span className="text-port-gray-900">{dossier.assure.telephone}</span>
                </div>
                <div className="flex items-center">
                  <Mail className="h-4 w-4 text-port-gray-400 mr-2" />
                  <a href={`mailto:${dossier.assure.email}`} className="text-atl-blue-600 hover:underline">
                    {dossier.assure.email}
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Informations tiers */}
          {dossier.tiers && (
            <div className="card">
              <div className="card-header">
                <h3 className="text-lg font-medium flex items-center">
                  <User className="h-5 w-5 mr-2" />
                  Tiers ({dossier.tiers.type === 'particulier' ? 'Particulier' : 'Société'})
                </h3>
              </div>
              <div className="card-body space-y-4">
                {dossier.tiers.type === 'particulier' ? (
                  <>
                    <div>
                      <label className="form-label">Nom complet</label>
                      <p className="text-port-gray-900 font-medium">
                        {dossier.tiers.prenom} {dossier.tiers.nom}
                      </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="form-label flex items-center">
                          <Calendar className="h-4 w-4 mr-1" />
                          Date de naissance
                        </label>
                        <p className="text-port-gray-900">
                          {dossier.tiers.dateNaissance ? new Date(dossier.tiers.dateNaissance).toLocaleDateString('fr-FR') : '-'}
                        </p>
                      </div>
                      <div>
                        <label className="form-label flex items-center">
                          <MapPin className="h-4 w-4 mr-1" />
                          Lieu de naissance
                        </label>
                        <p className="text-port-gray-900">{dossier.tiers.lieuNaissance || '-'}</p>
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <div>
                      <label className="form-label flex items-center">
                        <Building className="h-4 w-4 mr-1" />
                        Raison sociale
                      </label>
                      <p className="text-port-gray-900 font-medium">{dossier.tiers.raisonSociale}</p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="form-label">SIREN</label>
                        <p className="text-port-gray-900">{dossier.tiers.siren || '-'}</p>
                      </div>
                      <div>
                        <label className="form-label">SIRET</label>
                        <p className="text-port-gray-900">{dossier.tiers.siret || '-'}</p>
                      </div>
                    </div>
                  </>
                )}

                <div>
                  <label className="form-label">Adresse</label>
                  <p className="text-port-gray-900">{dossier.tiers.adresse}</p>
                </div>

                <div className="grid grid-cols-1 gap-3">
                  <div className="flex items-center">
                    <Phone className="h-4 w-4 text-port-gray-400 mr-2" />
                    <span className="text-port-gray-900">{dossier.tiers.telephone}</span>
                  </div>
                  <div className="flex items-center">
                    <Mail className="h-4 w-4 text-port-gray-400 mr-2" />
                    <a href={`mailto:${dossier.tiers.email}`} className="text-atl-blue-600 hover:underline">
                      {dossier.tiers.email}
                    </a>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Informations enquêteur */}
          <div className="card">
            <div className="card-header">
              <h3 className="text-lg font-medium flex items-center">
                <User className="h-5 w-5 mr-2" />
                Enquêteur Attribué
              </h3>
            </div>
            <div className="card-body space-y-4">
              <div>
                <label className="form-label">Nom</label>
                <p className="text-port-gray-900 font-medium">{dossier.enqueteur.nom}</p>
              </div>

              <div className="grid grid-cols-1 gap-3">
                <div className="flex items-center">
                  <Phone className="h-4 w-4 text-port-gray-400 mr-2" />
                  <span className="text-port-gray-900">{dossier.enqueteur.telephone}</span>
                </div>
                <div className="flex items-center">
                  <Mail className="h-4 w-4 text-port-gray-400 mr-2" />
                  <a href={`mailto:${dossier.enqueteur.email}`} className="text-atl-blue-600 hover:underline">
                    {dossier.enqueteur.email}
                  </a>
                </div>
              </div>

              <div>
                <label className="form-label">Date d'attribution</label>
                <p className="text-port-gray-900">
                  {new Date(dossier.dateAttribution).toLocaleDateString('fr-FR')}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'interactions' && (
        <div className="space-y-6">
          {/* Header avec filtres et nouveau commentaire */}
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-4">
              <h3 className="text-lg font-medium">Fil de discussion</h3>
              <div className="flex items-center space-x-2">
                <Filter className="h-4 w-4 text-port-gray-400" />
                <select
                  value={interactionFilter}
                  onChange={(e) => setInteractionFilter(e.target.value)}
                  className="form-select text-sm"
                >
                  <option value="all">Toutes les interactions</option>
                  <option value="commentaire">Commentaires</option>
                  <option value="document">Documents</option>
                  <option value="statut">Changements de statut</option>
                </select>
              </div>
            </div>
            
            <button
              onClick={() => setShowCommentForm(!showCommentForm)}
              className="btn btn-primary btn-sm"
            >
              <Plus className="h-4 w-4 mr-2" />
              Ajouter un commentaire
            </button>
          </div>

          {/* Formulaire nouveau commentaire */}
          {showCommentForm && (
            <div className="card">
              <div className="card-body">
                <form onSubmit={handleCommentSubmit}>
                  <div className="form-group">
                    <label className="form-label">Nouveau commentaire</label>
                    <textarea
                      value={newComment}
                      onChange={(e) => setNewComment(e.target.value)}
                      placeholder="Saisissez votre commentaire..."
                      className="form-textarea"
                      rows="3"
                      required
                    />
                  </div>
                  <div className="flex justify-end space-x-3">
                    <button
                      type="button"
                      onClick={() => setShowCommentForm(false)}
                      className="btn btn-secondary btn-sm"
                    >
                      Annuler
                    </button>
                    <button type="submit" className="btn btn-primary btn-sm">
                      <Send className="h-4 w-4 mr-2" />
                      Envoyer
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Liste des interactions */}
          <div className="space-y-4">
            {filteredInteractions.map(interaction => {
              const InteractionIcon = getInteractionIcon(interaction.type);
              return (
                <div key={interaction.id} className="card">
                  <div className="card-body">
                    <div className="flex items-start space-x-4">
                      <div className={`p-2 rounded-full ${
                        interaction.type === 'creation' ? 'bg-port-gray-100' :
                        interaction.type === 'commentaire' ? 'bg-atl-blue-100' :
                        interaction.type === 'document' ? 'bg-atl-green-100' :
                        'bg-yellow-100'
                      }`}>
                        <InteractionIcon className={`h-4 w-4 ${
                          interaction.type === 'creation' ? 'text-port-gray-600' :
                          interaction.type === 'commentaire' ? 'text-atl-blue-600' :
                          interaction.type === 'document' ? 'text-atl-green-600' :
                          'text-yellow-600'
                        }`} />
                      </div>
                      
                      <div className="flex-1">
                        <div className="flex justify-between items-start mb-2">
                          <div>
                            <span className="font-medium text-port-gray-900">
                              {interaction.auteur}
                            </span>
                            <span className="text-sm text-port-gray-500 ml-2">
                              {new Date(interaction.date).toLocaleString('fr-FR')}
                            </span>
                          </div>
                          <span className={`badge badge-sm ${
                            interaction.type === 'creation' ? 'badge-secondary' :
                            interaction.type === 'commentaire' ? 'badge-info' :
                            interaction.type === 'document' ? 'badge-success' :
                            'badge-warning'
                          }`}>
                            {interaction.type === 'creation' ? 'Système' :
                             interaction.type === 'commentaire' ? 'Commentaire' :
                             interaction.type === 'document' ? 'Document' :
                             'Statut'}
                          </span>
                        </div>
                        
                        <p className="text-port-gray-900 mb-3">{interaction.contenu}</p>
                        
                        {interaction.pieceJointe && (
                          <div className="flex items-center p-3 bg-port-gray-50 rounded-lg">
                            <Paperclip className="h-4 w-4 text-port-gray-400 mr-2" />
                            <div className="flex-1">
                              <span className="text-sm font-medium text-port-gray-900">
                                {interaction.pieceJointe.nom}
                              </span>
                              <span className="text-sm text-port-gray-500 ml-2">
                                ({interaction.pieceJointe.taille})
                              </span>
                            </div>
                            <button className="p-1 text-atl-blue-600 hover:bg-atl-blue-100 rounded">
                              <Download className="h-4 w-4" />
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {activeTab === 'documents' && (
        <div className="space-y-6">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-medium">Documents du Dossier</h3>
            <button className="btn btn-primary btn-sm">
              <Plus className="h-4 w-4 mr-2" />
              Ajouter un document
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {dossier.documentsTransmis.map(document => (
              <div key={document.id} className={`card ${
                document.obligatoire ? 'border-2 border-atl-blue-300' : ''
              }`}>
                <div className="card-body">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center flex-1">
                      <div className={`p-2 rounded-lg mr-3 ${
                        document.obligatoire ? 'bg-atl-blue-100' : 'bg-port-gray-100'
                      }`}>
                        <FileText className={`h-5 w-5 ${
                          document.obligatoire ? 'text-atl-blue-600' : 'text-port-gray-600'
                        }`} />
                      </div>
                      <div className="flex-1">
                        <h4 className="font-medium text-port-gray-900">{document.nom}</h4>
                        <p className="text-sm text-port-gray-500">{document.typeFichier}</p>
                        {document.obligatoire && (
                          <span className="inline-block mt-1 px-2 py-0.5 bg-atl-blue-100 text-atl-blue-700 text-xs font-medium rounded">
                            Requis pour attribution
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  <div className="text-sm text-port-gray-500 mb-3">
                    <p>Taille: {document.taille}</p>
                    <p>Ajouté le: {new Date(document.date).toLocaleDateString('fr-FR')}</p>
                  </div>
                  
                  <div className="flex space-x-2">
                    <button className="btn btn-secondary btn-sm flex-1">
                      <Eye className="h-4 w-4 mr-1" />
                      Voir
                    </button>
                    <button className="btn btn-primary btn-sm flex-1">
                      <Download className="h-4 w-4 mr-1" />
                      Télécharger
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default DossierDetail;
